/**
 * Browser-side API client.
 *
 * Adds the `X-CM-Request` header the CSRF middleware requires on every
 * mutation, sends cookies same-origin, and turns the server's error envelope
 * into a typed result instead of a thrown string. Screens branch on
 * `result.ok`, so a field-level validation response renders next to the field
 * rather than as a generic banner.
 */

export interface ApiFieldErrors {
  [field: string]: string;
}

export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; code: string; message: string; fields?: ApiFieldErrors; correlationId?: string };

/**
 * Short-lived reads for the tab that issued them.
 *
 * Two components mounting together used to send two identical GETs. The
 * in-flight map collapses those, and a successful response is reused for a
 * few seconds. Mutations clear the map, including a response that lands
 * after the write started, so a save is not hidden by a stale GET.
 *
 * Social connections re-check the provider on every read, the access
 * gate must notice a staff approval on the next navigation, and the
 * billing return screen polls a quote until it settles, so those are
 * not kept. A write also drops any read still in flight, so a later
 * GET does not join the request that started before the write.
 */
const READ_TTL_MS = 8_000;
const inflight = new Map<string, Promise<ApiResult<unknown>>>();
const cached = new Map<string, { at: number; result: ApiResult<unknown> }>();
let readEpoch = 0;

function uncached(path: string): boolean {
  return path.startsWith("/social-connections")
    || path.startsWith("/onboarding/access")
    // The billing return screen polls this until the ledger settles.
    || path.includes("/funding-quotes/");
}

function clearReads(): void {
  readEpoch += 1;
  cached.clear();
  inflight.clear();
}

/** Drops remembered GETs. Tests use it; a logout screen can too. */
export function clearApiCache(): void {
  clearReads();
  inflight.clear();
}

async function request<T>(
  method: string, path: string, body?: unknown,
): Promise<ApiResult<T>> {
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, {
      method,
      credentials: "same-origin",
      headers: {
        "content-type": "application/json",
        // Required by the CSRF middleware; a cross-origin request carrying
        // this header is preflighted and our CORS policy refuses it.
        "x-cm-request": "1",
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    return { ok: false, code: "NETWORK", message: "Could not reach the server. Check your connection." };
  }

  let payload: unknown;
  try {
    payload = await res.json();
  } catch {
    return { ok: false, code: "BAD_RESPONSE", message: "The server sent an unexpected response." };
  }

  if (res.ok) return { ok: true, data: payload as T };

  const err = (payload as { error?: Record<string, unknown> }).error;
  return {
    ok: false,
    code: typeof err?.["code"] === "string" ? (err["code"] as string) : "INTERNAL",
    message: typeof err?.["message"] === "string"
      ? (err["message"] as string)
      : "Something went wrong.",
    ...(err?.["fields"] ? { fields: err["fields"] as ApiFieldErrors } : {}),
    ...(err?.["correlationId"] ? { correlationId: err["correlationId"] as string } : {}),
  };
}

function remember(path: string, epoch: number, result: ApiResult<unknown>): void {
  if (epoch !== readEpoch || !result.ok || uncached(path)) return;
  cached.set(path, { at: Date.now(), result });
}

function get<T>(path: string): Promise<ApiResult<T>> {
  const hit = cached.get(path);
  if (hit && Date.now() - hit.at < READ_TTL_MS) return Promise.resolve(hit.result as ApiResult<T>);
  const pending = inflight.get(path);
  if (pending) return pending as Promise<ApiResult<T>>;
  const epoch = readEpoch;
  const promise = request<T>("GET", path).then((result) => {
    if (inflight.get(path) === promise) inflight.delete(path);
    remember(path, epoch, result);
    return result;
  });
  inflight.set(path, promise as Promise<ApiResult<unknown>>);
  return promise;
}

async function write<T>(method: "POST" | "PUT" | "PATCH", path: string, body?: unknown): Promise<ApiResult<T>> {
  clearReads();
  const result = await request<T>(method, path, body ?? {});
  clearReads();
  return result;
}

export const api = {
  get,
  post: <T>(path: string, body?: unknown) => write<T>("POST", path, body),
  put: <T>(path: string, body?: unknown) => write<T>("PUT", path, body),
  /** Partial update. The draft autosave sends only the fields that changed. */
  patch: <T>(path: string, body?: unknown) => write<T>("PATCH", path, body),
  /**
   * Raw bytes, same-origin. Cover photos cannot PUT to the R2 URL from the
   * browser: CSP connect-src is 'self', and the quarantine bucket is not
   * a same-origin host.
   */
  putBytes: <T>(path: string, body: Blob, mime: string) => putBytes<T>(path, body, mime),
  /** Multipart form. Do not set content-type - the boundary must come from the browser. */
  postForm: <T>(path: string, body: FormData) => postForm<T>(path, body),
};

async function putBytes<T>(path: string, body: Blob, mime: string): Promise<ApiResult<T>> {
  clearReads();
  try {
    let res: Response;
    try {
      res = await fetch(`/api/v1${path}`, {
        method: "PUT",
        credentials: "same-origin",
        headers: {
          "content-type": mime,
          "x-cm-request": "1",
        },
        body,
      });
    } catch {
      return { ok: false, code: "NETWORK", message: "Could not reach the server. Check your connection." };
    }

    let payload: unknown;
    try {
      payload = await res.json();
    } catch {
      return { ok: false, code: "BAD_RESPONSE", message: "The server sent an unexpected response." };
    }

    if (res.ok) return { ok: true, data: payload as T };

    const err = (payload as { error?: Record<string, unknown> }).error;
    return {
      ok: false,
      code: typeof err?.["code"] === "string" ? (err["code"] as string) : "INTERNAL",
      message: typeof err?.["message"] === "string"
        ? (err["message"] as string)
        : "Something went wrong.",
      ...(err?.["fields"] ? { fields: err["fields"] as ApiFieldErrors } : {}),
      ...(err?.["correlationId"] ? { correlationId: err["correlationId"] as string } : {}),
    };
  } finally {
    clearReads();
  }
}

async function postForm<T>(path: string, body: FormData): Promise<ApiResult<T>> {
  clearReads();
  try {
    let res: Response;
    try {
      res = await fetch(`/api/v1${path}`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "x-cm-request": "1" },
        body,
      });
    } catch {
      return { ok: false, code: "NETWORK", message: "Could not reach the server. Check your connection." };
    }

    let payload: unknown;
    try {
      payload = await res.json();
    } catch {
      return { ok: false, code: "BAD_RESPONSE", message: "The server sent an unexpected response." };
    }

    if (res.ok) return { ok: true, data: payload as T };

    const err = (payload as { error?: Record<string, unknown> }).error;
    return {
      ok: false,
      code: typeof err?.["code"] === "string" ? (err["code"] as string) : "INTERNAL",
      message: typeof err?.["message"] === "string"
        ? (err["message"] as string)
        : "Something went wrong.",
      ...(err?.["fields"] ? { fields: err["fields"] as ApiFieldErrors } : {}),
      ...(err?.["correlationId"] ? { correlationId: err["correlationId"] as string } : {}),
    };
  } finally {
    clearReads();
  }
}

export interface SessionResponse {
  authenticated: boolean;
  audience: string;
  signIn?: { method: string };
  user?: {
    id: string;
    email: string;
    displayName: string | null;
    status: string;
    emailVerified: boolean;
    createdAt?: string | null;
    avatarHref?: string | null;
  };
}

export const getSession = () => api.get<SessionResponse>("/auth/session");
